import * as PIXI from 'pixi.js';

import { assertNumberBetween, assertHasOwnProperties } from '../../validators';

import { int2rgba } from '../../libs/videoData';

import { AlphaAttribute } from '../attributes';
import type { DestroyOptions } from '../../types';
import { Config } from '../../video-data/config';
import type { LoadSettings } from '../serializableMixin';
import { DynamicStylingMixin } from '../dynamicStylingMixin';
import { ColorStop } from './ColorStop';
import type { BaseAssetSettings } from './BaseAsset';
import { BaseAsset } from './BaseAsset';

type GradientAssetSettings = BaseAssetSettings & {
    rectWidth?: number;
    rectHeight?: number;
    fillAlpha?: number;
};

/**
 * Creates a gradient filled sprite. The gradient is created along a square of
 * dimensions 100px by 100px.
 */
export class GradientAsset extends DynamicStylingMixin(BaseAsset) {
    override _classType = Config.GRADIENT_ASSET_TYPE;

    #colorStops = [
        new ColorStop({ parent: this, redrawParentOnUpdate: true }),
        new ColorStop({ parent: this, redrawParentOnUpdate: true }),
    ];
    @AlphaAttribute()
    accessor fillAlpha = 1;
    #rectHeight = 10;
    #rectWidth = 10;
    #endPoint = new PIXI.Point(1, 1);
    #startPoint = new PIXI.Point(0, 0);

    #CANVAS_SIZE = 100;
    #canvas: HTMLCanvasElement = null;
    #sprite: PIXI.Sprite = null;

    constructor(container, settings: GradientAssetSettings) {
        super(container, settings);

        this.#canvas = document.createElement('canvas');
        this.#canvas.width = this.#CANVAS_SIZE;
        this.#canvas.height = this.#CANVAS_SIZE;

        this.#rectWidth = settings.rectWidth ?? this.#rectWidth;
        this.#rectHeight = settings.rectHeight ?? this.#rectHeight;
        this.fillAlpha = settings.fillAlpha ?? this.fillAlpha;
    }

    override destroy(options?: DestroyOptions) {
        super.destroy(options);
        this.#sprite = null;
    }

    override get failedToLoad() {
        return false;
    }

    override get isLoaded() {
        return true;
    }

    /**
     * This asset doesn't have to load anything remotely so it should "always load"
     */
    override get shouldLoad() {
        return true;
    }

    override isMorphMatch(other: BaseAsset): boolean {
        const otherGradient = other as GradientAsset;
        if (!Config.isGradientAsset(otherGradient)) {
            return false;
        }

        if (
            this.startPoint.x !== otherGradient.startPoint.x ||
            this.startPoint.y !== otherGradient.startPoint.y ||
            this.endPoint.x !== otherGradient.endPoint.x ||
            this.endPoint.y !== otherGradient.endPoint.y
        ) {
            return false;
        }

        const sameColorStops =
            this.colorStops.length === otherGradient.colorStops.length &&
            this.colorStops.every((stop, i) => {
                const otherStop = otherGradient.colorStops[i];
                return (
                    stop.color === otherStop.color &&
                    stop.offset === otherStop.offset &&
                    stop.alpha === otherStop.alpha
                );
            });

        if (!sameColorStops) {
            return false;
        }

        const samePosition =
            this.transformation.position.x === otherGradient.transformation.position.x &&
            this.transformation.position.y === otherGradient.transformation.position.y;
        const sameSize =
            this.rectWidth === otherGradient.rectWidth &&
            this.rectHeight === otherGradient.rectHeight;
        const sameAlpha =
            this.alpha === otherGradient.alpha && this.fillAlpha === otherGradient.fillAlpha;

        if (samePosition && sameSize && sameAlpha) {
            return false;
        }

        return true;
    }

    get startPoint() {
        return this.#startPoint;
    }

    get colorStops(): ColorStop[] {
        return this.#colorStops;
    }

    /**
     * For documentation, see serializableMixin.save
     */
    _colorStopsCustomSave(allProperties, clone, returnTemplateReference, skipTemplateAttributes) {
        return this.#colorStops.map((colorStop) =>
            colorStop.save(allProperties, clone, returnTemplateReference, skipTemplateAttributes),
        );
    }

    /**
     * For documentation, see serializableMixin.load
     */
    _colorStopsCustomLoad(colorStopObjects, settings: LoadSettings) {
        if (!colorStopObjects) {
            return;
        }

        this.#colorStops.splice(colorStopObjects.length);

        colorStopObjects.forEach((colorStopObject, index) => {
            const colorStopInstance =
                this.#colorStops[index] ??
                new ColorStop({
                    parent: this,
                    redrawParentOnUpdate: true,
                });
            colorStopInstance.load(colorStopObject, settings);
            this.#colorStops[index] = colorStopInstance;
        });
    }

    set startPoint(point) {
        assertHasOwnProperties(point, ['x', 'y']);
        assertNumberBetween(point.x, 0, 1);
        assertNumberBetween(point.y, 0, 1);

        if (this.#startPoint.x === point.x && this.#startPoint.y === point.y) {
            return;
        }

        this.#startPoint = point;

        this.requiresRedraw = true;
    }

    get endPoint() {
        return this.#endPoint;
    }

    set endPoint(point) {
        assertHasOwnProperties(point, ['x', 'y']);
        assertNumberBetween(point.x, 0, 1);
        assertNumberBetween(point.y, 0, 1);

        if (this.#endPoint.x === point.x && this.#endPoint.y === point.y) {
            return;
        }
        this.#endPoint = point;

        this.requiresRedraw = true;
    }

    get canvas() {
        return this.#canvas;
    }

    get sprite() {
        return this.#sprite;
    }

    get rectWidth() {
        return this.#rectWidth;
    }

    set rectWidth(value) {
        if (this.#rectWidth === value) {
            return;
        }
        assertNumberBetween(value, 0, Infinity);
        this.#rectWidth = value;
        this.requiresRedraw = true;
    }

    get rectHeight() {
        return this.#rectHeight;
    }

    set rectHeight(value) {
        if (this.rectHeight === value) {
            return;
        }
        assertNumberBetween(value, 0, Infinity);
        this.#rectHeight = value;
        this.requiresRedraw = true;
    }

    override _build() {
        this.clearAnimation();

        this.#destroySprite();
        this.#sprite = new PIXI.Sprite(this.#createGradientTexture());
        this.#sprite.width = this.#rectWidth;
        this.#sprite.height = this.#rectHeight;
        this._addInternalChild(this.#sprite);

        if (this.hasDynamicStyle || this.hasMorphAnimation) {
            this._prepareDynamicStylingForUpdate();
            this.applyTransformation();
            this.applyAnimation();
            this._updateDynamicStyling();
        } else {
            this.applyTransformation();
            this.applyAnimation();
        }

        this.applyIntroAnimations();
        this.applyOutroAnimations();
        this.animationManager.setInitValues();
    }

    override _draw() {
        if (!this.#sprite) {
            return;
        }
        this.#sprite.alpha = this.fillAlpha;
        this.applyTransformation();
    }

    #createGradientTexture() {
        const context = this.#canvas.getContext('2d');
        context.clearRect(0, 0, this.#canvas.width, this.#canvas.height);
        const gradient = context.createLinearGradient(
            this.#startPoint.x * this.#CANVAS_SIZE,
            this.#startPoint.y * this.#CANVAS_SIZE,
            this.#endPoint.x * this.#CANVAS_SIZE,
            this.#endPoint.y * this.#CANVAS_SIZE,
        );

        this.#colorStops.forEach((colorStop) => {
            const rgbaString = int2rgba(colorStop.color, colorStop.alpha);
            gradient.addColorStop(colorStop.offset, rgbaString);
        });

        context.fillStyle = gradient;
        context.fillRect(0, 0, this.#CANVAS_SIZE, this.#CANVAS_SIZE);
        return PIXI.Texture.from(this.#canvas);
    }

    #destroySprite() {
        if (!this.#sprite) {
            return;
        }

        this.#sprite.destroy(true);
        this.#sprite = null;
    }

    override get propertiesToSerialize() {
        return super.propertiesToSerialize.concat([
            'mask',
            'rectWidth',
            'rectHeight',
            'colorStops',
            'startPoint',
            'endPoint',
            'fillAlpha',
        ]);
    }
}
